import React from "react";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { AvailabilityGraphD3 } from "../home/schedule_components/visual_components/AvailabilityGraphD3";
import {
  buildRoleTypeNameMap,
  convertToQuarterIndex,
  getDayIndexFromDateString,
  getMemberRoleTypes,
  isActiveEmployee,
  registerRoleTypeName,
  safeLocaleCompare,
} from "./utils";

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function buildRoleTypeOptions(planningData, roleTypeNameMap) {
  const options = new Set();
  const addOption = (value) => {
    const resolved = registerRoleTypeName(value, roleTypeNameMap);
    if (resolved) {
      options.add(resolved);
    }
  };

  planningData.roleTypes?.forEach((role) => {
    addOption(role);
    if (role?.role_type) {
      addOption(role.role_type);
    }
  });
  planningData.membersById?.forEach((member) => {
    getMemberRoleTypes(member, roleTypeNameMap).forEach((roleType) => {
      addOption(roleType);
    });
  });
  planningData.futureForecast?.forEach((entry) => {
    addOption(entry?.role_type);
  });
  return [
    "All departments",
    ...Array.from(options).sort((a, b) => safeLocaleCompare(a, b)),
  ];
}

function computeWeeksCount(futureRange) {
  if (!futureRange?.start || !futureRange?.end) {
    return 4;
  }
  const diffMs = futureRange.end.getTime() - futureRange.start.getTime();
  const weeks = Math.max(Math.round(diffMs / (7 * 24 * 60 * 60 * 1000)), 1);
  return weeks;
}

function getHeatMapColor(bufferPercentage, laborNeeded) {
  if (laborNeeded === 0) {
    return "no-demand";
  }
  if (bufferPercentage <= 20) {
    return "critical";
  }
  if (bufferPercentage <= 30) {
    return "severe";
  }
  if (bufferPercentage <= 40) {
    return "warning";
  }
  if (bufferPercentage <= 50) {
    return "adequate";
  }
  if (bufferPercentage <= 60) {
    return "plenty";
  }
  return "surplus";
}

function buildAvailabilityHeatmapData(
  planningData,
  roleTypeNameMap,
  selectedRoleType
) {
  if (!planningData) {
    return {
      data: [],
      minOpenTime: 0,
      maxCloseTime: 24,
      weeksCount: 0,
      hasData: false,
    };
  }

  const weeksCount = Math.max(computeWeeksCount(planningData.futureRange), 1);
  const members = Array.from(planningData.membersById?.values() || []);
  const activeMembers = members.filter((member) => isActiveEmployee(member));
  const filteredMembers =
    selectedRoleType === "All departments"
      ? activeMembers
      : activeMembers.filter((member) =>
          getMemberRoleTypes(member, roleTypeNameMap).includes(
            selectedRoleType
          )
        );
  const eligibleMemberIds = new Set(filteredMembers.map((member) => member.id));

  const availabilityCounts = Array.from({ length: 7 }, () => []);
  const availabilityDaySet = new Set();
  let availabilityMinQuarter = Number.POSITIVE_INFINITY;
  let availabilityMaxQuarter = Number.NEGATIVE_INFINITY;

  planningData.defaultAvailability?.forEach((entry) => {
    const memberId = entry?.store_team_member;
    if (!eligibleMemberIds.has(memberId)) {
      return;
    }
    const dowIndex = Number(entry?.dow || 1) - 1;
    if (dowIndex < 0 || dowIndex >= 7) {
      return;
    }

    const label = DAY_LABELS[dowIndex];
    const applyWindow = (startRaw, endRaw) => {
      const start = convertToQuarterIndex(startRaw);
      const end = convertToQuarterIndex(endRaw);
      if (start === null || end === null || end <= start) {
        return;
      }
      if (label) {
        availabilityDaySet.add(label);
      }
      for (let idx = Math.max(start, 0); idx < end; idx += 1) {
        availabilityCounts[dowIndex][idx] =
          (availabilityCounts[dowIndex][idx] || 0) + 1;
        availabilityMinQuarter = Math.min(availabilityMinQuarter, idx);
        availabilityMaxQuarter = Math.max(availabilityMaxQuarter, idx);
      }
    };

    applyWindow(entry?.available_from, entry?.available_to);
    applyWindow(entry?.available_from_2, entry?.available_to_2);
  });

  const forecastSums = Array.from({ length: 7 }, () => []);
  const forecastCounts = Array.from({ length: 7 }, () => []);
  const forecastsByDateQuarter = new Map();
  const demandDaySet = new Set();
  let demandMinQuarter = Number.POSITIVE_INFINITY;
  let demandMaxQuarter = Number.NEGATIVE_INFINITY;

  planningData.futureForecast?.forEach((entry) => {
    const entryRoleType = registerRoleTypeName(
      entry?.role_type,
      roleTypeNameMap
    );
    if (
      selectedRoleType !== "All departments" &&
      entryRoleType !== selectedRoleType
    ) {
      return;
    }
    const quarterIndex = convertToQuarterIndex(entry?.qh_num);
    const hours = Number(entry?.hours || 0);
    if (quarterIndex === null || quarterIndex < 0) {
      return;
    }
    const employeesNeeded = Math.max(hours, 0) * 4;
    const key = `${entry?.date}_${quarterIndex}`;
    forecastsByDateQuarter.set(
      key,
      (forecastsByDateQuarter.get(key) || 0) + employeesNeeded
    );
  });

  forecastsByDateQuarter.forEach((totalNeeded, key) => {
    const [dateStr, quarterStr] = key.split("_");
    const dowIndex = getDayIndexFromDateString(dateStr);
    const quarterIndex = Number(quarterStr);
    if (
      dowIndex === null ||
      dowIndex < 0 ||
      dowIndex >= 7 ||
      Number.isNaN(quarterIndex) ||
      quarterIndex < 0
    ) {
      return;
    }
    forecastSums[dowIndex][quarterIndex] =
      (forecastSums[dowIndex][quarterIndex] || 0) + totalNeeded;
    forecastCounts[dowIndex][quarterIndex] =
      (forecastCounts[dowIndex][quarterIndex] || 0) + 1;

    if (totalNeeded > 0) {
      const label = DAY_LABELS[dowIndex];
      if (label) {
        demandDaySet.add(label);
      }
      demandMinQuarter = Math.min(demandMinQuarter, quarterIndex);
      demandMaxQuarter = Math.max(demandMaxQuarter, quarterIndex);
    }
  });

  const hasDemandWindow =
    Number.isFinite(demandMinQuarter) && Number.isFinite(demandMaxQuarter);
  const hasAvailabilityWindow =
    Number.isFinite(availabilityMinQuarter) &&
    Number.isFinite(availabilityMaxQuarter);

  const baseMinQuarter = hasDemandWindow
    ? demandMinQuarter
    : hasAvailabilityWindow
    ? availabilityMinQuarter
    : 0;
  const baseMaxQuarter = hasDemandWindow
    ? demandMaxQuarter
    : hasAvailabilityWindow
    ? availabilityMaxQuarter
    : 95;

  const paddedStartQuarter = Math.max(
    (Number.isFinite(baseMinQuarter) ? baseMinQuarter : 0) - 1,
    0
  );
  const paddedEndQuarter = Number.isFinite(baseMaxQuarter)
    ? Math.max(baseMaxQuarter + 1, 95)
    : 95;

  const startQuarter = paddedStartQuarter;
  const endQuarter = Math.max(paddedEndQuarter, paddedStartQuarter + 3);

  const rows = [];

  DAY_LABELS.forEach((label, dowIndex) => {
    for (let quarter = startQuarter; quarter <= endQuarter; quarter += 1) {
      const peopleAvailable = availabilityCounts[dowIndex][quarter] || 0;
      const totalNeeded = forecastSums[dowIndex][quarter] || 0;
      const observations = forecastCounts[dowIndex][quarter] || 0;
      const laborAvg =
        observations > 0 ? Math.round(totalNeeded / observations) : 0;

      const buffer = peopleAvailable - laborAvg;
      const bufferPercentage =
        peopleAvailable > 0 ? (buffer / peopleAvailable) * 100 : 0;

      rows.push({
        date: label,
        qh_num: quarter / 4,
        quarterIndex: quarter,
        labor_needed: laborAvg,
        people_available: peopleAvailable,
        buffer,
        bufferPercentage,
        color: getHeatMapColor(bufferPercentage, laborAvg),
      });
    }
  });

  const allowedDaySet = demandDaySet.size ? demandDaySet : availabilityDaySet;
  const filteredRows = rows.filter(
    (row) => allowedDaySet.size === 0 || allowedDaySet.has(row.date)
  );

  const minOpenTime =
    (Number.isFinite(baseMinQuarter) ? baseMinQuarter : 0) / 4;
  const maxCloseTime = Math.max(
    minOpenTime + 1,
    (Number.isFinite(baseMaxQuarter) ? baseMaxQuarter + 1 : endQuarter + 1) / 4
  );
  const hasData = filteredRows.some(
    (entry) => entry.people_available > 0 || entry.labor_needed > 0
  );

  const data = filteredRows.map(({ quarterIndex, ...rest }) => rest);
  const domainCandidates = allowedDaySet.size
    ? DAY_LABELS.filter((label) => allowedDaySet.has(label))
    : DAY_LABELS.filter((label) =>
        filteredRows.some((row) => row.date === label)
      );
  const xDomain = domainCandidates.length ? domainCandidates : DAY_LABELS;

  return { data, minOpenTime, maxCloseTime, weeksCount, hasData, xDomain };
}

function HeadcountAvailabilityHeatmap({
  data,
  minOpenTime,
  maxCloseTime,
  xDomain,
}) {
  const containerRef = React.useRef(null);
  const chartId = React.useMemo(
    () => `headcount-availability-${Math.random().toString(36).slice(2, 9)}`,
    []
  );

  React.useEffect(() => {
    if (containerRef.current) {
      AvailabilityGraphD3(
        containerRef.current,
        data,
        minOpenTime,
        maxCloseTime,
        chartId,
        {
          tickFormat: (value) => value,
          xDomain,
          height: 520,
          tooltipFormat: (value) => value,
        }
      );
    }
  }, [data, minOpenTime, maxCloseTime, chartId, xDomain]);

  return (
    <div className="w-full">
      <section className="Main" style={{ textAlign: "center" }}>
        <div id={chartId} ref={containerRef} />
      </section>
    </div>
  );
}

const LegendTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: 480,
  },
});

function AvailabilityLegend() {
  const legendItems = [
    {
      label: "Surplus",
      range: "> 60%",
      color: "#69B34C",
      description: "Excellent coverage with ample flexibility",
    },
    {
      label: "Plenty",
      range: "50-60%",
      color: "#ACB334",
      description: "Good coverage with flexibility",
    },
    {
      label: "Adequate",
      range: "40-50%",
      color: "#FAB733",
      description: "Sufficient coverage with limited flexibility",
    },
    {
      label: "Warning",
      range: "30-40%",
      color: "#FF8E15",
      description: "Tight coverage - consider adding staff",
    },
    {
      label: "Severe",
      range: "20-30%",
      color: "#FF4E11",
      description: "Critical shortage - urgent action needed",
    },
    {
      label: "Critical",
      range: "≤ 20%",
      color: "#FF0D0D",
      description: "Understaffed - minimal buffer",
    },
  ];

  return (
    <div className="rounded-xl p-4 border border-slate-200 bg-white">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold text-slate-700">Coverage Legend</h4>
        <LegendTooltip
          placement="left"
          title={
            <div className="space-y-2">
              <p className="font-medium">Buffer Percentage:</p>
              <p>
                The buffer shows how much excess capacity you have beyond forecasted demand.
              </p>
              <div className="mt-3 pt-3 border-t border-slate-300">
                <div className="flex items-center justify-center gap-2 text-xs font-mono">
                  <span>buffer %</span>
                  <span>=</span>
                  <div className="text-center">
                    <div className="border-b border-slate-300 pb-1">
                      available − needed
                    </div>
                    <div className="pt-1">available</div>
                  </div>
                  <span>× 100</span>
                </div>
              </div>
              <p className="mt-3 text-xs text-slate-300">
                Example: If 10 employees are available and 8 are needed, buffer = 20%
              </p>
            </div>
          }
        >
          <InfoOutlinedIcon className="text-slate-400 cursor-help" fontSize="small" />
        </LegendTooltip>
      </div>
      <div className="grid grid-cols-3 gap-x-4 gap-y-2">
        {legendItems.map((item) => (
          <div
            key={item.label}
            className="flex items-center gap-2 text-xs"
          >
            <div
              className="w-6 h-6 rounded shadow-sm flex-shrink-0 border border-slate-200"
              style={{ backgroundColor: item.color }}
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline gap-1.5">
                <span className="font-semibold text-slate-700">{item.label}</span>
                <span className="text-slate-500 font-mono text-[10px]">{item.range}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function HeadcountPlanningAvailability({ planningData }) {
  const roleTypeNameMap = React.useMemo(() => {
    if (!planningData) {
      return new Map();
    }
    if (planningData.roleTypeNameMap instanceof Map) {
      return planningData.roleTypeNameMap;
    }
    return buildRoleTypeNameMap(planningData.roleTypes);
  }, [planningData]);

  const roleTypeOptions = React.useMemo(() => {
    if (!planningData) {
      return ["All departments"];
    }
    return buildRoleTypeOptions(planningData, roleTypeNameMap);
  }, [planningData, roleTypeNameMap]);

  const [selectedRoleType, setSelectedRoleType] = React.useState(
    () => roleTypeOptions[0]
  );

  React.useEffect(() => {
    if (!roleTypeOptions.includes(selectedRoleType)) {
      setSelectedRoleType(roleTypeOptions[0]);
    }
  }, [roleTypeOptions, selectedRoleType]);

  const heatmapData = React.useMemo(
    () =>
      buildAvailabilityHeatmapData(
        planningData,
        roleTypeNameMap,
        selectedRoleType
      ),
    [planningData, roleTypeNameMap, selectedRoleType]
  );

  const { data, minOpenTime, maxCloseTime, weeksCount, hasData, xDomain } =
    heatmapData;
  return (
    <div className="space-y-6">
      <section className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
        <div className="flex lg:flex-col items-start justify-between gap-6 mb-2">
          <div className="flex-1">
            <div className="mb-3">
              <h3 className="text-lg font-semibold text-slate-900">
                Availability coverage
              </h3>
              <p className="text-sm text-slate-600">
                {weeksCount > 0
                  ? `Average buffer across the next ${weeksCount} weeks of forecasted demand.`
                  : "Average buffer across upcoming demand."}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-slate-600">
                Department
              </span>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <Select
                  value={selectedRoleType}
                  onChange={(event) => setSelectedRoleType(event.target.value)}
                  displayEmpty
                  sx={{
                    borderRadius: "12px",
                    backgroundColor: "white",
                    ".MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#cbd5f5",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#6d28d9",
                    },
                    "& .MuiSelect-select": {
                      paddingTop: "8px",
                      paddingBottom: "8px",
                    },
                  }}
                >
                  {roleTypeOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          </div>

          <div className="lg:w-full w-auto">
            <AvailabilityLegend />
          </div>
        </div>

        {hasData ? (
          <HeadcountAvailabilityHeatmap
            data={data}
            minOpenTime={minOpenTime}
            maxCloseTime={maxCloseTime}
            xDomain={xDomain}
          />
        ) : (
          <div className="py-12 text-center text-slate-500">
            No availability or forecast data found for the selected department.
          </div>
        )}
      </section>
    </div>
  );
}

export default HeadcountPlanningAvailability;
